/**
 * Named "Preview with sample data" scenarios (ENG-2039), so QA can walk the
 * page through one situation at a time on redesign and dev, where staging's
 * frozen database cannot show "what is true right now".
 *
 * `mixed` is the original design-mockup sample and stays the default. Every
 * other scenario feeds every sampled surface (troubleshootingSampleData.ts)
 * and also overlays the one site's arm state, camera signals and saved-login
 * result, and the tunnels' handshake times, so the Diagnosis findings follow.
 */
export type SampleScenarioKey =
  | "mixed"
  | "healthy"
  | "disarmed"
  | "inactive"
  | "connector-silent"
  | "no-video"
  | "wg-stale"
  | "s2s-down"
  | "receiver-refusing"
  | "smtp-mismatch"
  | "milestone-login-refused";

export interface SampleScenario {
  key: SampleScenarioKey;
  label: string;
  /** One line: what the page should show for it. */
  description: string;
}

export const DEFAULT_SAMPLE_SCENARIO: SampleScenarioKey = "mixed";

export const SAMPLE_SCENARIOS: SampleScenario[] = [
  {
    key: "mixed",
    label: "Mixed sample",
    description:
      "The design mockup: a site-to-site link down, a receiver timing out, two quiet cameras, a recent settings change.",
  },
  {
    key: "healthy",
    label: "Healthy site, alarms flowing",
    description:
      "Armed, connector reporting, video on every camera, links up, receivers accepting, alerts arriving. No findings.",
  },
  {
    key: "disarmed",
    label: "Disarmed site",
    description:
      "The site is disarmed, so nothing arriving is expected: one 'disarmed' note, no fault.",
  },
  {
    key: "inactive",
    label: "Inactive site",
    description:
      "The site is marked inactive and won't arm from its schedule: one 'inactive' note, no fault.",
  },
  {
    key: "connector-silent",
    label: "Connector silent",
    description:
      "Armed, but the connector's last heartbeat was 40 minutes ago: Actuate's side to fix.",
  },
  {
    key: "no-video",
    label: "All cameras without video",
    description:
      "Connector reporting, but no camera has delivered video for 90 minutes: the customer's side.",
  },
  {
    key: "wg-stale",
    label: "WireGuard peer stale",
    description:
      "Every WireGuard tunnel's last handshake was 41 minutes ago and the cameras behind it have no video.",
  },
  {
    key: "s2s-down",
    label: "Site-to-site VPN down",
    description:
      "Both IPsec tunnels to the customer's peer are down and alerts have stopped.",
  },
  {
    key: "receiver-refusing",
    label: "Receiver not accepting",
    description:
      "Alerts are generated, but the Immix receiver refuses connections and test alarms fail.",
  },
  {
    key: "smtp-mismatch",
    label: "SMTP filename rule mismatch",
    description:
      "Images arrive by email, but the filename rule matches no camera, so they are filed as unidentified.",
  },
  {
    key: "milestone-login-refused",
    label: "Milestone saved-login refused",
    description:
      "The VMS refuses the saved login: every camera stopped together and Test saved login reads 'Unauthorized'.",
  },
];

export const isSampleScenarioKey = (
  value: unknown
): value is SampleScenarioKey =>
  SAMPLE_SCENARIOS.some((scenario) => scenario.key === value);

export const sampleScenarioByKey = (key: SampleScenarioKey): SampleScenario =>
  SAMPLE_SCENARIOS.find((scenario) => scenario.key === key) ??
  SAMPLE_SCENARIOS[0];
